import React, { useState } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import {
  useCustomerInfo,
  usePaymentTerms,
  useShippingMethods,
  useCreateCustomerInfo,
  useUpdateCustomerInfo,
  useDeleteCustomerInfo,
} from '../../hooks/useCustomerInfo';
import { useCustomers } from '../../hooks/useCustomers';
import { CustomerInfo } from '../../api/services/customerInfoService';

export default function CustomerConfig() {
  const { data: customerInfo, isLoading } = useCustomerInfo();
  const { data: paymentTerms } = usePaymentTerms();
  const { data: shippingMethods } = useShippingMethods();
  const { data: allCustomers } = useCustomers();
  const createCustomerInfo = useCreateCustomerInfo();
  const updateCustomerInfo = useUpdateCustomerInfo();
  const deleteCustomerInfo = useDeleteCustomerInfo();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<CustomerInfo | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const recordsPerPage = 20;

  // Filter out customers that are already in the configuration
  const availableCustomers = allCustomers?.filter(
    (customer) => !customerInfo?.some((ci) => ci.CustomerId === customer.CustomerID)
  );

  // Filter customer info by search term
  const filteredCustomerInfo = customerInfo?.filter((item) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.CustomerId.toLowerCase().includes(search) ||
      item.CustomerName.toLowerCase().includes(search)
    );
  }) || [];

  // Calculate pagination
  const totalPages = Math.ceil(filteredCustomerInfo.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedCustomerInfo = filteredCustomerInfo.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEdit = (item: CustomerInfo) => {
    setEditingId(item.Id);
    setEditFormData({ ...item });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData(null);
  };

  const handleSave = async () => {
    if (editFormData && editingId) {
      await updateCustomerInfo.mutateAsync({
        id: editingId,
        data: {
          AutoCheck: editFormData.AutoCheck,
          EdiIntegration: editFormData.EdiIntegration,
          Dropship: editFormData.Dropship,
          DropshipCharge: editFormData.DropshipCharge,
          Freight: editFormData.Freight,
          DefaultShippingMethod: editFormData.DefaultShippingMethod,
          PaymentTerm: editFormData.PaymentTerm,
        },
      });
      setEditingId(null);
      setEditFormData(null);
    }
  };

  const handleDelete = async (item: CustomerInfo) => {
    if (window.confirm(`Are you sure you want to remove ${item.CustomerName}?`)) {
      await deleteCustomerInfo.mutateAsync(item.Id);
    }
  };

  const handleAdd = async () => {
    if (selectedCustomerId) {
      const customer = allCustomers?.find((c) => c.CustomerID === selectedCustomerId);
      if (customer) {
        await createCustomerInfo.mutateAsync({
          CustomerId: customer.CustomerID,
          CustomerName: customer.Name || customer.CustomerID,
          AutoCheck: false,
          EdiIntegration: false,
          Dropship: false,
          DropshipCharge: 0,
          Freight: 0,
          DefaultShippingMethod: null,
          PaymentTerm: '',
        });
        setSelectedCustomerId('');
      }
    }
  };

  const updateEditField = (field: keyof CustomerInfo, value: any) => {
    if (editFormData) {
      setEditFormData({ ...editFormData, [field]: value });
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="p-6">Loading...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="w-full">
        <div className="bg-white dark:bg-gray-800 shadow">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Customers</h1>
          </div>

          {/* Search and Add Customer Form */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center gap-4 mb-4">
              <input
                type="text"
                placeholder="Search by Customer ID or Name..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex items-center gap-4">
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select a customer to add...</option>
                {availableCustomers?.map((customer) => (
                  <option key={customer.CustomerID} value={customer.CustomerID}>
                    {customer.Name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                disabled={!selectedCustomerId}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>

          <div className="p-4">
            <table className="w-full table-auto border-collapse border border-gray-300 dark:border-gray-600">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Customer ID
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Customer Name
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Auto check
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    EDI Integration
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Dropship
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Dropship Charge
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Freight Charge/Item
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Default Shipping Method
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Payment Terms
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {paginatedCustomerInfo.map((item) => {
                  const isEditing = editingId === item.Id;
                  const displayItem = isEditing && editFormData ? editFormData : item;

                  return (
                    <tr key={item.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
                        {item.CustomerId}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
                        {item.CustomerName}
                      </td>
                      <td className="px-3 py-2 text-center border border-gray-300 dark:border-gray-600">
                        <input
                          type="checkbox"
                          checked={displayItem.AutoCheck}
                          disabled={!isEditing}
                          onChange={(e) => updateEditField('AutoCheck', e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2 text-center border border-gray-300 dark:border-gray-600">
                        <input
                          type="checkbox"
                          checked={displayItem.EdiIntegration}
                          disabled={!isEditing}
                          onChange={(e) => updateEditField('EdiIntegration', e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2 text-center border border-gray-300 dark:border-gray-600">
                        <input
                          type="checkbox"
                          checked={displayItem.Dropship}
                          disabled={!isEditing}
                          onChange={(e) => updateEditField('Dropship', e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2 border border-gray-300 dark:border-gray-600">
                        <input
                          type="number"
                          value={displayItem.DropshipCharge}
                          disabled={!isEditing}
                          onChange={(e) => updateEditField('DropshipCharge', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                        />
                      </td>
                      <td className="px-3 py-2 border border-gray-300 dark:border-gray-600">
                        <input
                          type="number"
                          value={displayItem.Freight}
                          disabled={!isEditing}
                          onChange={(e) => updateEditField('Freight', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                        />
                      </td>
                      <td className="px-3 py-2 border border-gray-300 dark:border-gray-600">
                        <select
                          value={displayItem.DefaultShippingMethod || ''}
                          disabled={!isEditing}
                          onChange={(e) =>
                            updateEditField(
                              'DefaultShippingMethod',
                              e.target.value ? parseInt(e.target.value) : null
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                        >
                          <option value=""></option>
                          {shippingMethods?.map((sm) => (
                            <option key={sm.Id} value={sm.Id}>
                              {sm.Name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 border border-gray-300 dark:border-gray-600">
                        <select
                          value={displayItem.PaymentTerm || ''}
                          disabled={!isEditing}
                          onChange={(e) => updateEditField('PaymentTerm', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900"
                        >
                          <option value=""></option>
                          {paymentTerms?.map((pt) => (
                            <option key={pt} value={pt}>
                              {pt}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-sm space-x-2 border border-gray-300 dark:border-gray-600">
                        {!isEditing ? (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={handleSave}
                              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredCustomerInfo.length)} of {filteredCustomerInfo.length} entries
                {searchTerm && ` (filtered from ${customerInfo?.length || 0} total)`}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current page
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 border rounded ${
                          currentPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return <span key={page} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
